import * as yup from "yup";

/**
 * Canonical, full set of coupon types known to the system. This is the
 * source of truth for:
 *   - the `CouponType` TS union (see `couponListTypes.ts`),
 *   - the All Coupons filter dropdown,
 *   - the Edit Coupon modal (so legacy CASHBACK coupons can still be edited
 *     for other fields like expiry / value),
 *   - URL hydration validation in `useCouponFilters`.
 *
 * CASHBACK is intentionally kept here even though it's no longer creatable
 * via the UI — the backend may still return existing CASHBACK coupons that
 * predate the order-scoped issuance flow, and admins must be able to see /
 * filter / edit them.
 *
 * For the Create Coupon modal use `CREATABLE_COUPON_TYPE_OPTIONS` below.
 */
export const COUPON_TYPE_OPTIONS = [
  "REFERRAL",
  "GIVEAWAY",
  "REFERRAL_BONUS",
  "SIGNUP_BONUS",
  "FIRST_ORDER_DISCOUNT",
  "CASHBACK",
] as const;

/**
 * Coupon types that admins can create through the Create Coupon modal.
 *
 * CASHBACK is intentionally excluded — cashback is now exclusively
 * order-scoped via the Issue Cashback flow (POST /v1/coupon/admin/cashback/
 * {buyerId}). The Create Coupon path doesn't enforce a completed-order
 * requirement, so allowing CASHBACK here would bypass the business rule.
 */
export const CREATABLE_COUPON_TYPE_OPTIONS = [
  "REFERRAL",
  "GIVEAWAY",
  "REFERRAL_BONUS",
  "SIGNUP_BONUS",
  "FIRST_ORDER_DISCOUNT",
] as const;

/** Max number of coupons a single bulk-create call can produce. */
export const COUPON_BULK_MAX = 100;

const isNotInPast = (value: string | undefined): boolean => {
  if (!value) return true; // empty is handled by `.required`
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() >= startOfToday.getTime();
};

export const validationSchema = yup.object().shape({
  expiry_date: yup
    .string()
    .required("Expiry date is required")
    .test("not-past", "Expiry date cannot be before today", isNotInPast),

  couponType: yup
    .string()
    .oneOf([...CREATABLE_COUPON_TYPE_OPTIONS], "Invalid coupon type")
    .required("Coupon type is required"),

  couponValueType: yup
    .string()
    .oneOf(["AMOUNT", "PERCENTAGE"], "Invalid value type")
    .required("Value type is required"),

  couponAmount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? undefined : value
    )
    .when("couponValueType", {
      is: "AMOUNT",
      then: (schema) =>
        schema
          .typeError("Coupon amount must be a number")
          .required("Coupon amount is required")
          .positive("Amount must be greater than 0"),
      otherwise: (schema) => schema.notRequired(),
    }),

  couponDiscount: yup
    .number()
    .transform((value, originalValue) =>
      originalValue.trim() === "" ? undefined : value
    )
    .when("couponValueType", {
      is: "PERCENTAGE",
      then: (schema) =>
        schema
          .typeError("Discount must be a number")
          .required("Coupon discount is required")
          .min(1, "Discount must be at least 1%")
          .max(100, "Discount cannot exceed 100%"),
      otherwise: (schema) => schema.notRequired(),
    }),

  count: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .typeError("Count must be a number")
    .required("Count is required")
    .integer("Count must be a whole number")
    .min(1, "Must create at least 1 coupon")
    .max(COUPON_BULK_MAX, `Cannot create more than ${COUPON_BULK_MAX} at once`),

  cooldownPeriod: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .typeError("Cooldown period must be a number")
    .required("Cooldown period is required")
    .integer("Must be a whole number")
    .min(1, "Must be at least 1 hour"),

  activeDuration: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .typeError("Active duration must be a number")
    .required("Active duration is required")
    .integer("Must be a whole number")
    .min(1, "Must be at least 1 hour"),

  vendorIncentiveType: yup
    .string()
    .oneOf(["AMOUNT", "PERCENTAGE"], "Invalid vendor incentive type")
    .required("Vendor incentive type is required"),

  vendorIncentiveAmount: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .when("vendorIncentiveType", {
      is: "AMOUNT",
      then: (schema) =>
        schema
          .typeError("Vendor incentive amount must be a number")
          .required("Vendor incentive amount is required")
          .min(0, "Cannot be negative"),
      otherwise: (schema) => schema.notRequired(),
    }),

  vendorIncentivePercentage: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .when("vendorIncentiveType", {
      is: "PERCENTAGE",
      then: (schema) =>
        schema
          .typeError("Vendor incentive percentage must be a number")
          .required("Vendor incentive percentage is required")
          .min(0, "Cannot be negative")
          .max(100, "Cannot exceed 100%"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

/**
 * Edit schema mirrors create's value-type rules but:
 *  - reuses the not-past expiry rule from `validationSchema` (consistent UX)
 *  - has no `count` (not editable)
 *  - has no create-only fields: cooldown / active duration / vendor incentives
 *  - re-widens `couponType` back to `COUPON_TYPE_OPTIONS` so existing CASHBACK
 *    coupons can still pass validation when an admin edits other fields.
 *    (Create narrows to `CREATABLE_COUPON_TYPE_OPTIONS` — Edit must NOT.)
 */
export const editCouponValidationSchema = validationSchema.shape({
  count: yup.mixed().notRequired(),
  couponType: yup
    .string()
    .oneOf([...COUPON_TYPE_OPTIONS], "Invalid coupon type")
    .required("Coupon type is required"),
  cooldownPeriod: yup.mixed().notRequired(),
  activeDuration: yup.mixed().notRequired(),
  vendorIncentiveAmount: yup.mixed().notRequired(),
  vendorIncentivePercentage: yup.mixed().notRequired(),
  vendorIncentiveType: yup.mixed().notRequired(),
});
